import { Box, Dialog, Typography } from '@mui/material';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { ToastState } from '../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurButton from '../aur-button/AurButton';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import { AurTextField } from '../aur-text-field/AurTextField';
import AurToast from '../aur-toast/AurToast';

interface ValuesType {
  firstName: string;
  lastName: string;
}

interface AurAddCustodianProps {
  isOpen: boolean;
  keepMounted: boolean;
  onClose: () => void;
  onAddClick: (value: ValuesType) => void;
  isLoading?: boolean;
  toast?: ToastState;
  setToast?: React.Dispatch<React.SetStateAction<ToastState>>;
}

const AurAddCustodianModel: React.FC<AurAddCustodianProps> = ({
  isOpen,
  onClose,
  onAddClick,
  isLoading = false,
  toast,
  setToast,
  ...other
}) => {
  const [values, setValues] = useState<ValuesType>({
    firstName: '',
    lastName: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    setValues((prev) => ({...prev, [name]: value}));
  };

  const isDisabled = useMemo(() => {
    return !values.firstName?.trim() || !values.lastName?.trim();
  }, [values.firstName, values.lastName]);

  return (
    <div data-testid="add-custodian-model">
      <Dialog
        fullScreen={true}
        open={isOpen}
        PaperProps={{className: 'overlay'}}
        aria-labelledby="add-custodian-title"
        aria-describedby="add-custodian-desc"
        {...other}
      >
        <div className="add-custodian-container">
          <div className="title-container">
            {
              <Typography
                id="add-custodian-title"
                className="typo-text headline3_size head-text"
              >
                {t(`addCustodian.title`)}
              </Typography>
            }
            {
              <Typography
                id="add-custodian-desc"
                className="typo-text body2_size sub-head-text"
              >
                {t(`addCustodian.subTitle`)}
              </Typography>
            }
          </div>
          <div className="input-container">
            <AurTextField
              fullWidth
              label={t('addCustodian.firstName')}
              className="aur-textfield"
              variant="filled"
              value={values.firstName}
              onChange={handleChange}
              ariaLabel={t(`addCustodian.firstName`)}
              data-testid={'first-name'}
              name="firstName"
              tabIndex={-1} // disabled outer div of input field by set -1
              autoFocus
            />

            <AurTextField
              fullWidth
              label={t('addCustodian.lastName')}
              className="aur-textfield"
              variant="filled"
              value={values.lastName}
              onChange={handleChange}
              ariaLabel={t(`addCustodian.lastName`)}
              data-testid={'last-name'}
              name="lastName"
              tabIndex={-1} // disabled outer div of input field by set -1
            />
            <Box className="loading-indicator">
              <AurLoadingIndicator show={isLoading} />
            </Box>
          </div>
          <div className="button-container">
            <AurButton
              onClick={() => onClose()}
              type="secondary"
              size="Large"
              label={t('addCustodian.close')}
              classNames="buttons"
              dataTestId="close-button"
              aria-label={t('addCustodian.close')}
              aria-describedby="close-button-hint"
            />
            <span id="close-button-hint" style={{display: 'none'}}>
              {t('addCustodian.closeButtonHintText')}
            </span>
            <AurButton
              onClick={() => onAddClick(values)}
              type="submit"
              size="Large"
              label={t('addCustodian.add')}
              disabled={isDisabled || isLoading}
              classNames="buttons"
              dataTestId="add-button"
              aria-label={t('addCustodian.add')}
              aria-describedby="add-button-hint"
            />
            <span id="add-button-hint" style={{display: 'none'}}>
              {t('addCustodian.addButtonHintText')}
            </span>
          </div>
        </div>
        <AurToast
          open={toast?.open ?? false}
          message={toast?.message ?? ''}
          severity={toast?.severity}
          hideDuration={5000}
          horizontalPosition="center"
          verticalPosition="bottom"
          onClose={() => setToast?.((prev) => ({...prev, open: false}))}
        />
      </Dialog>
    </div>
  );
};

export default AurAddCustodianModel;
